const TECHNICAL_MESSAGE_PATTERN =
  /(https?:\/\/|localhost|typeerror|stack|trace|cors|unexpected token|json|api\/|econn|err_|sql|mysql|sequelize|query failed)/i;

function normalizeFieldName(field) {
  return String(field || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");
}

function getFieldLevelMessage(field, fallbackMessage) {
  const normalizedField = normalizeFieldName(field);

  if (normalizedField === "emailorusername" || normalizedField === "identifier") {
    return "Please enter your email or username.";
  }

  if (normalizedField === "email") {
    return /valid email/i.test(String(fallbackMessage || ""))
      ? "Please enter a valid email address."
      : "Please enter your email.";
  }

  if (normalizedField === "password") {
    return /at least 8/i.test(String(fallbackMessage || ""))
      ? "Password must be at least 8 characters."
      : "Please enter your password.";
  }

  if (normalizedField === "phone" || normalizedField === "numbere164" || normalizedField === "phonenumber") {
    return "Please enter a valid phone number.";
  }

  if (normalizedField === "name" || normalizedField === "fullname") {
    return "Please enter a name.";
  }

  return "";
}

function getValidationMessageFromDetails(details) {
  const issues = Array.isArray(details?.errors)
    ? details.errors
    : Array.isArray(details)
      ? details
      : [];

  for (const issue of issues) {
    const fieldMessage = getFieldLevelMessage(issue?.field, issue?.message);

    if (fieldMessage) {
      return fieldMessage;
    }
  }

  const firstIssue = issues.find((issue) => String(issue?.message || "").trim());

  if (!firstIssue) {
    return "Please check the highlighted fields and try again.";
  }

  if (TECHNICAL_MESSAGE_PATTERN.test(String(firstIssue.message))) {
    return "Please check the highlighted fields and try again.";
  }

  return String(firstIssue.message).trim();
}

export function getApiErrorMessage(payload, status, fallbackMessage = "") {
  const backendMessage = String(
    payload?.message || payload?.error || payload?.detail || fallbackMessage || "",
  ).trim();
  const validationMessage = getValidationMessageFromDetails(payload);

  if (validationMessage) {
    return validationMessage;
  }

  if (status === 401) {
    if (/invalid|incorrect|credential|password|username|email/i.test(backendMessage)) {
      return "Invalid login credentials.";
    }

    return "Your session has expired. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (status === 404) {
    return "The requested record was not found.";
  }

  if (status === 409) {
    if (/delete|referenced/i.test(backendMessage)) {
      return "This record cannot be deleted right now.";
    }

    return "This record already exists.";
  }

  if (status === 422) {
    return validationMessage || "Please check the highlighted fields and try again.";
  }

  if (status >= 500) {
    return "Something went wrong on the server.";
  }

  if (!backendMessage) {
    return "";
  }

  if (TECHNICAL_MESSAGE_PATTERN.test(backendMessage)) {
    return "";
  }

  return backendMessage;
}

export function sanitizeUiErrorMessage(message, options = {}) {
  const text = String(message || "").trim();
  const { code, status, details } = options;

  if (code === "ABORTED") {
    return "Request cancelled.";
  }

  if (code === "EMPTY_RESPONSE") {
    return "No data available right now.";
  }

  if (code === "TIMEOUT") {
    return "Request timed out. Please try again.";
  }

  if (code === "NETWORK_ERROR") {
    return "Network error. Please check your connection.";
  }

  if (status) {
    const apiMessage = getApiErrorMessage(details, status, text);

    if (apiMessage) {
      return apiMessage;
    }
  }

  if (!text) {
    return "Unable to load data right now. Please try again.";
  }

  if (TECHNICAL_MESSAGE_PATTERN.test(text)) {
    return "Unable to load data right now. Please try again.";
  }

  return text;
}

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(sanitizeUiErrorMessage(message, options));
    this.name = "ApiError";
    this.rawMessage = message;
    this.status = options.status || null;
    this.code = options.code || "API_ERROR";
    this.url = options.url || "";
    this.details = options.details ?? null;
    this.meta = options.meta ?? null;
    this.isAbort = Boolean(options.isAbort);
    this.cause = options.cause;
  }
}

export function createApiError(message, options = {}) {
  return new ApiError(message, options);
}

export function normalizeApiError(error, fallback = {}) {
  if (error instanceof ApiError) {
    return error;
  }

  if (error?.name === "AbortError") {
    return new ApiError("Request cancelled.", {
      code: "ABORTED",
      isAbort: true,
      ...fallback,
      cause: error,
    });
  }

  if (error?.name === "TimeoutError") {
    return new ApiError("Request timed out.", {
      code: "TIMEOUT",
      ...fallback,
      cause: error,
    });
  }

  if (error instanceof TypeError) {
    return new ApiError("Network request failed.", {
      code: "NETWORK_ERROR",
      ...fallback,
      cause: error,
    });
  }

  return new ApiError(
    error?.message || "Unable to load data right now. Please try again.",
    {
      code: "UNKNOWN_ERROR",
      ...fallback,
      cause: error,
    },
  );
}

export function isAbortError(error) {
  return Boolean(error?.isAbort || error?.name === "AbortError");
}
